import { useEffect, useRef } from "react"

const stats = [
  { num: "7+", label: "Years of design leadership experience" },
  { num: "3→9", label: "Design team scaled at OKX in 1 year" },
  { num: "187M", label: "Users impacted by products at Grab" },
  { num: "14+", label: "Years building and shipping product" },
]

const topics = [
  "Building design cultures that scale without losing craft",
  "Design systems as organisational alignment tools",
  "Hiring and developing senior designers in high-growth environments",
  "AI-native design workflows for product teams",
  "Web3 product design — incentives, discovery, and trust",
  "Cross-functional leadership with PMs, engineers, and executives",
  "Transitioning from IC to design leadership roles",
  "Designing for scale in emerging markets",
  "Bridging Web2 and Web3 — making Web3 more accessible for everyone",
]

const dimensions = [
  {
    label: "Craft",
    items: ["Product design 0→1", "Product redesign", "Design iteration", "Design systems"],
  },
  {
    label: "Vision & Strategy",
    items: ["Future product direction", "Connecting ecosystem dots", "Roadmap influence", "Design workshops"],
  },
  {
    label: "Leadership",
    items: ["Design management", "Principal IC work", "Coaching & mentorship", "Empowered team building"],
  },
  {
    label: "AI Transformation",
    items: ["AI-native workflow design", "Team enablement & upskilling", "Tooling evaluation & adoption", "Prototyping with AI"],
    accent: true,
  },
]

function parseStat(str) {
  if (str.includes("→")) return { type: "static" }
  const match = str.match(/^(\d+)(.*)$/)
  if (match) return { type: "count", end: parseInt(match[1]), suffix: match[2] || "" }
  return { type: "static" }
}

function AnimatedStat({ num }) {
  const ref = useRef(null)
  const parsed = parseStat(num)

  useEffect(() => {
    if (parsed.type !== "count") return
    const el = ref.current
    if (!el) return
    const duration = 1400
    const start = performance.now()

    function update(now) {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      el.textContent = Math.round(eased * parsed.end) + parsed.suffix
      // frameStyle: fade to 50% opacity between 20–80% of animation, like react-animated-number
      el.style.opacity = progress < 0.2 ? 1 : progress < 0.8 ? 0.45 : 0.45 + ((progress - 0.8) / 0.2) * 0.55
      if (progress < 1) requestAnimationFrame(update)
    }

    el.textContent = "0" + parsed.suffix
    el.style.opacity = 1
    const raf = requestAnimationFrame(update)
    return () => cancelAnimationFrame(raf)
  }, [])

  if (parsed.type === "static") return <div className="leadership-stat-num">{num}</div>
  return <div className="leadership-stat-num" ref={ref}>0{parsed.suffix}</div>
}

export default function Leadership() {
  return (
    <div className="page-panel">
      <div className="panel-inner">

        <div className="panel-header">
          <div className="panel-tag">Leadership</div>
          <h2 className="panel-title">Team &amp;<br />Culture</h2>
          <p className="panel-subtitle">
            Design leadership is about creating the conditions for great work —
            not just doing it. Here's how I think about building teams,
            growing craft, and driving organisational impact.
          </p>
        </div>

        <div className="section-heading">At a Glance</div>

        <div className="leadership-grid" style={{ marginBottom: "80px" }}>
          {stats.map((s) => (
            <div key={s.num} className="leadership-stat">
              <AnimatedStat num={s.num} />
              <div className="leadership-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="section-heading">Design Quality — Four Dimensions</div>

        <div className="dimensions-grid" style={{ marginBottom: "80px" }}>
          {dimensions.map((d) => (
            <div key={d.label} className={`dimensions-card${d.accent ? " dimensions-card--accent" : ""}`}>
              <div className="dimensions-label">{d.label}</div>
              <ul className="dimensions-items">
                {d.items.map((item) => (
                  <li key={item} className="dimensions-item">— {item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="section-heading">Topics I Think &amp; Talk About</div>

        <ul className="leadership-topics">
          {topics.map((topic, i) => (
            <li key={i} className="leadership-topic">
              <span className="leadership-topic-index">{String(i + 1).padStart(2, "0")}</span>
              {topic}
            </li>
          ))}
        </ul>

        <div className="section-divider" />

        <p className="panel-subtitle" style={{ maxWidth: "480px" }}>
          Open to speaking, mentoring, and design leadership conversations.
          <br /><br />
          <a href="mailto:ricetsengtw@gmail.com" style={{ color: "var(--fg)", textDecoration: "underline", textDecorationColor: "var(--border-strong)", textUnderlineOffset: "3px" }}>
            Get in touch →
          </a>
        </p>

      </div>
    </div>
  )
}
